import { useEffect, useMemo, useRef, useState } from 'react';
import { Icons, fmt } from '@/components/primitives';
import { useProductions, useVenues } from '@/api/hooks';
import { adaptProductionForLegacyManage, VENUE_UNKNOWN, type LegacyManageProduction } from '@/lib/manage-screen-adapters';

/** venue id -> name, for `adaptProductionForLegacyManage`. Both wizard routes
 *  block on `venuesList` in their loader, so this is populated by first render;
 *  the production object is snapshotted when the operator picks it and never
 *  revisited, which is why the fetch cannot be left in the background. */
export function useVenueNames(): ReadonlyMap<string, string | null> {
  const { data: venueRows = [] } = useVenues();
  return useMemo(
    () => new Map(venueRows.map(venue => [venue.id, venue.name])),
    [venueRows],
  );
}

export interface ProductionPickerProps {
  /** The production the wizard holds, or null before one is picked. */
  selected: LegacyManageProduction | null | undefined;
  onSelect: (production: LegacyManageProduction) => void;
}

export function ProductionPicker({ selected, onSelect }: ProductionPickerProps) {
  const venueNames = useVenueNames();
  const { data: productionRows = [] } = useProductions();
  const PRODUCTIONS = useMemo(
    () => productionRows.map(production => adaptProductionForLegacyManage(production, venueNames)),
    [productionRows, venueNames],
  );
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const filtered = PRODUCTIONS.filter(s => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return s.title.toLowerCase().includes(needle) || (s.venue ?? '').toLowerCase().includes(needle) || s.genre.toLowerCase().includes(needle);
  });

  return (
    <div>
      <div className="production-search">
        <div className="production-search-input">
          <span style={{color:'var(--ink-3)'}}>{Icons.search}</span>
          {/* Short enough for a phone (F5 UX P5); the count is printed under the box. The label
              names what it matches, which the long placeholder used to. */}
          <input ref={inputRef} aria-label="Search productions by title, venue or genre" placeholder="Search productions" value={q} onChange={e=>setQ(e.target.value)}/>
          {q && <button className="production-search-clear" onClick={()=>setQ('')} aria-label="Clear">{Icons.x}</button>}
        </div>
        <div className="production-search-meta">
          {filtered.length === PRODUCTIONS.length ? `${PRODUCTIONS.length} productions` : `${filtered.length} of ${PRODUCTIONS.length} productions`}
        </div>
      </div>

      <div className="production-list">
        {filtered.map(s => (
          <div key={s.id} className={`production-row ${selected?.id === s.id ? 'selected' : ''}`} role="button" tabIndex={0} aria-pressed={selected?.id === s.id} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();onSelect(s);}}} onClick={()=>onSelect(s)}>
            <div className="production-row-poster" style={{background:s.posterBg,color:s.posterFg}}>
              <span style={{fontFamily:'var(--font-display)',fontWeight:800,fontSize:9,letterSpacing:'.04em',lineHeight:1,textAlign:'center',padding:'0 2px'}}>{s.title.split(' ')[0]}</span>
            </div>
            <div className="production-row-main">
              <div className="production-row-title">{s.title}</div>
              <div className="production-row-meta">{s.venue ?? VENUE_UNKNOWN} · {s.genre} · {s.dates}</div>
            </div>
            <div className="production-row-stat">
              <div className="production-row-stat-val">{Math.round(s.capacity*100)}%</div>
              <div className="production-row-stat-lbl">CAP</div>
            </div>
            <div className="production-row-stat">
              <div className="production-row-stat-val">{fmt.$(s.weekGross)}</div>
              <div className="production-row-stat-lbl">/WK</div>
            </div>
            <div className="production-row-stat">
              <div className="production-row-stat-val">${s.avgPrice}</div>
              <div className="production-row-stat-lbl">AVG TIX</div>
            </div>
            <div className="production-row-action">
              {selected?.id === s.id ? <span className="production-row-check">{Icons.check}</span> : <span style={{color:'var(--ink-3)'}}>{Icons.chev_r}</span>}
            </div>
          </div>
        ))}
        {!filtered.length && (
          <div className="production-row-empty">
            No productions match <b>"{q}"</b>.
            <button className="btn btn-sm" style={{marginLeft:10}} onClick={()=>setQ('')}>Reset</button>
          </div>
        )}
      </div>
    </div>
  );
}
